/**
 * Рендерит один `NarrativeAspect` как секцию страницы.
 *
 * Карточка минималистична: заголовок (эмодзи + название + small status-icon),
 * затем абзац LLM-summary (если есть), затем структурные blocks.
 *
 * Сознательно убраны:
 *  - aspect_id монospace под title (девелоперский шум для PM-аудитории)
 *  - цветной borderLeft + бэкграунд на summary (over-design, бэйдж статуса
 *    уже достаточно)
 *  - текстовая часть бэйджа «fresh»/«нет summary» — оставлен только эмодзи-
 *    индикатор, который и так понятен (✨ свежий / ⚠ устаревший / ? нет)
 */

import { useState } from 'react';
import { ActionIcon, Box, Card, Collapse, Group, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconChevronDown, IconCircleCheck, IconExternalLink, IconLink } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import type { NarrativeAspect } from '@pflow/shared/v2';
import { NarrativeBlockView } from './NarrativeBlockView';
import { MarkdownText } from './MarkdownText';
import { AudienceTable } from './AudienceTable';
import { DESKTOP_QUERY } from '../lib/breakpoints';

// При ≥AUDIENCE_TABLE_THRESHOLD Сегментах в `narr.canvas.audience`
// переключаемся на comparison-таблицу — стек блоков по странице
// каждый перестаёт читаться, нужны параметры рядом. Считаются ТОЛЬКО
// блоки-Сегменты: в аспекте профиля есть и Работа/Боли/Выгоды — колонками
// сравнения они быть не должны (рендерятся обычным списком под таблицей).
const AUDIENCE_TABLE_THRESHOLD = 3;

/**
 * Аспекты «следа решений»: здесь key/value поля — это и есть содержание
 * (вопрос → выбрали → отвергли → почему → вернёмся если), а не деталь
 * поверх рассказа. Для них поля рендерятся прямо в карточке, и детали
 * раскрыты сразу: свернуть след решения в «Показать детали» — значит
 * спрятать ровно то, ради чего читатель пришёл.
 *
 * Остальные аспекты остаются как были: рассказ + свёрнутые структурные
 * блоки без полей.
 */
const TRAIL_ASPECTS: ReadonlySet<string> = new Set([
    'narr.project.frames',
    'narr.project.decisions',
    'narr.project.open-questions',
]);

/** Показывать ли key/value поля entity-блоков этого аспекта (см. TRAIL_ASPECTS). */
export function aspectShowsFields(aspectId: string): boolean {
    return TRAIL_ASPECTS.has(aspectId);
}

/**
 * Собственные tour-цели у ключевых аспектов — тур ведёт по задаче
 * «что мы решили и почему», а не по абстрактному «блоку нарратива».
 * Ключи — часть контракта TOUR_TARGETS (см. lib/tours.ts).
 */
const TOUR_KEY: Readonly<Record<string, string>> = {
    'narr.project.frames':         'aspect-frames',
    'narr.project.decisions':      'aspect-decisions',
    'narr.project.evidence':       'aspect-evidence',
    'narr.project.open-questions': 'aspect-open-questions',
};

/**
 * Ключ tour-цели карточки. Канвасные аспекты получают общий
 * `aspect-canvas` (первое совпадение = первый блок первого Канваса) —
 * тур показывает на нём, во что решения превратились в продукте.
 * Остальные — общий `aspect-card`.
 */
function tourKeyOf(aspect: NarrativeAspect): string {
    return TOUR_KEY[aspect.id]
        ?? (aspect.scope.kind === 'canvas' ? 'aspect-canvas' : 'aspect-card');
}

interface Props {
    aspect: NarrativeAspect;
}

interface SummaryStatus {
    icon:    string;
    color:   string;
    tooltip: string;
}

function statusOf(aspect: NarrativeAspect): SummaryStatus {
    if (!aspect.summary) {
        return { icon: '?', color: 'var(--mantine-color-gray-5)', tooltip: 'Нет нарратива — нужно сгенерировать' };
    }
    if (aspect.summary.status === 'fresh') {
        return { icon: '✨', color: 'var(--mantine-color-green-5)', tooltip: `Свежий · ${aspect.summary.generatedBy}` };
    }
    return { icon: '⚠', color: 'var(--mantine-color-yellow-5)', tooltip: 'Устарел — модель изменилась после генерации' };
}

export function NarrativeAspectView({ aspect }: Props): JSX.Element {
    const isDesktop = useMediaQuery(DESKTOP_QUERY, true) ?? true;
    const status = statusOf(aspect);
    const empty  = aspect.blocks.length === 0;
    const trail  = TRAIL_ASPECTS.has(aspect.id);

    // Главная часть — заголовок + summary. Структурные blocks (цитаты,
    // сущности, поля) прячем в collapsible. Без summary блоки и есть
    // основной контент — раскрыты по умолчанию. У аспектов «следа решений»
    // блоки раскрыты всегда: LLM-абзац там — оглавление, а не замена.
    const [expanded, setExpanded] = useState(trail || !aspect.summary);

    return (
        <Card
            id={anchorId(aspect)}
            data-tour={tourKeyOf(aspect)}
            withBorder
            radius="md"
            p={isDesktop ? 'md' : 'sm'}
            mb={isDesktop ? 'md' : 'sm'}
            style={{ scrollMarginTop: 80 }}
        >
            {/* Шапка: эмодзи + название + точечный status-индикатор справа.
               Align center, чтобы все элементы по центру одной линии. */}
            <Group justify="space-between" align="center" wrap="nowrap" gap="xs" mb={empty && !aspect.summary ? 0 : 'sm'}>
                <Text fw={600} fz={isDesktop ? 'md' : 'sm'} truncate style={{ minWidth: 0, flex: 1 }}>
                    {aspect.title}
                </Text>
                <AspectActions aspect={aspect} />
                <Tooltip label={status.tooltip} withArrow position="left">
                    <Text fz="md" style={{ color: status.color, cursor: 'help', lineHeight: 1 }}>
                        {status.icon}
                    </Text>
                </Tooltip>
            </Group>

            {/* Summary — обычный параграф, без декоративной рамки. Italic
               отделяет нарратив от структурных данных, а лёгкий отступ
               снизу — от blocks. Подпись «кто/когда» в самом конце мелким
               шрифтом, чтобы не отвлекать от текста. */}
            {aspect.summary && (
                <Box mb={empty ? 0 : 'sm'}>
                    <MarkdownText text={aspect.summary.text} />
                    <Text fz="xs" c="dimmed" mt={4}>
                        {aspect.summary.generatedBy} · {formatDate(aspect.summary.generatedAt)}
                    </Text>
                </Box>
            )}

            {empty && !aspect.summary && (
                <Text fz="sm" c="dimmed" fs="italic">Нет данных по этому нарративу.</Text>
            )}

            {!empty && (
                <>
                    <UnstyledButton
                        onClick={() => setExpanded(e => !e)}
                        aria-expanded={expanded}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, width: 'fit-content' }}
                    >
                        <Text fz="xs" c="dimmed">
                            {expanded
                                ? (trail ? 'Свернуть' : 'Скрыть детали')
                                : `${trail ? 'Развернуть' : 'Показать детали'} · ${aspect.blocks.length}`}
                        </Text>
                        <IconChevronDown
                            size={12}
                            style={{
                                color:      'var(--mantine-color-dimmed)',
                                transform:  expanded ? 'rotate(180deg)' : 'none',
                                transition: 'transform 150ms ease',
                            }}
                        />
                    </UnstyledButton>

                    <Collapse in={expanded}>
                        <Box mt="sm">
                            <AspectBlocks aspect={aspect} />
                        </Box>
                    </Collapse>
                </>
            )}
        </Card>
    );
}

/**
 * Блоки аспекта. Спецслучай «Профиль клиента» (`narr.canvas.audience`):
 * при ≥3 Сегментах сегментные блоки уходят в comparison-таблицу, а
 * остальные (Работа, Боли, Выгоды) — обычным списком под ней.
 */
function AspectBlocks({ aspect }: { aspect: NarrativeAspect }): JSX.Element {
    const showFields = TRAIL_ASPECTS.has(aspect.id);
    if (aspect.id === 'narr.canvas.audience') {
        const segments = aspect.blocks.filter(b => b.entity?.type === 'Сегмент');
        if (segments.length >= AUDIENCE_TABLE_THRESHOLD) {
            const rest = aspect.blocks.filter(b => b.entity?.type !== 'Сегмент');
            return (
                <Stack gap="sm">
                    <AudienceTable blocks={segments} />
                    {rest.length > 0 && (
                        <Stack gap={4}>
                            {rest.map((b, i) => <NarrativeBlockView key={i} block={b} />)}
                        </Stack>
                    )}
                </Stack>
            );
        }
    }
    return (
        <Stack gap={4}>
            {aspect.blocks.map((b, i) => (
                <NarrativeBlockView key={i} block={b} showFields={showFields} />
            ))}
        </Stack>
    );
}

/**
 * Кнопки действий аспекта: permalink (URL с #якорем — «обсудить конкретный
 * блок» без «проскролль до третьего») и «→ issue» (github/issues/new с
 * предзаполненными заголовком и deep-link'ом — issue создаёт сам пользователь
 * под своим аккаунтом, zero-backend).
 */
function AspectActions({ aspect }: { aspect: NarrativeAspect }): JSX.Element | null {
    const { owner, repo } = useParams();
    const [copied, setCopied] = useState(false);
    if (!owner || !repo) return null;

    const permalink = `${location.origin}${location.pathname}${location.search}#${anchorId(aspect)}`;

    const copyLink = (): void => {
        void navigator.clipboard.writeText(permalink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    const issueUrl = ((): string => {
        const scopePart = aspect.scope.kind === 'canvas' ? ` (Канвас «${aspect.scope.canvas}»)` : '';
        const title = `[pflow] ${aspect.title}${scopePart}: `;
        // Маркер проекта: в мультипроектном репо вкладка «Вопросы» фильтрует
        // issues по subpath из текущего URL.
        const subpath = new URLSearchParams(location.search).get('path');
        const pathMarker = subpath ? `\n<!-- pflow-path: ${subpath} -->\n` : '';
        const body  = `Контекст: [${aspect.title}${scopePart}](${permalink})\n${pathMarker}\n<!-- опиши идею/вопрос -->\n`;
        const u = new URL(`https://github.com/${owner}/${repo}/issues/new`);
        u.searchParams.set('title', title);
        u.searchParams.set('body', body);
        u.searchParams.set('labels', 'idea');
        return u.toString();
    })();

    return (
        <Group gap={2} wrap="nowrap" data-no-print data-tour="aspect-actions">
            <Tooltip label={copied ? 'Скопировано' : 'Скопировать ссылку на блок'} withArrow>
                <ActionIcon variant="subtle" color="gray" size="sm" onClick={copyLink} aria-label="Ссылка на блок">
                    {copied ? <IconCircleCheck size={14} /> : <IconLink size={14} />}
                </ActionIcon>
            </Tooltip>
            <Tooltip label="Зафиксировать идею как GitHub issue" withArrow>
                <ActionIcon
                    component="a"
                    href={issueUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Создать issue"
                >
                    <IconExternalLink size={14} />
                </ActionIcon>
            </Tooltip>
        </Group>
    );
}

/** Стабильный якорь для scroll-to и URL-фрагмента. */
export function anchorId(aspect: NarrativeAspect): string {
    const scope = aspect.scope.kind === 'project'
        ? 'project'
        : `canvas-${slug(aspect.scope.canvas)}`;
    return `aspect-${scope}-${aspect.id.replace(/\./g, '-')}`;
}

function slug(s: string): string {
    return s.toLowerCase().replace(/[^a-zа-я0-9]+/gi, '-').replace(/^-|-$/g, '').slice(0, 60);
}

function formatDate(iso: string): string {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toISOString().slice(0, 10);
    } catch {
        return iso;
    }
}
